/**
 * Server-sent events framing (WHATWG HTML § Server-sent events), with no
 * dependency of its own: the gateway writes with it, the clients read with it,
 * and a browser can import it through `@sunucupark/shared/lite` without pulling
 * zod into its first download (ADR 0006, ADR 0008).
 */

/** Media type of the stream. */
export const EVENT_STREAM_CONTENT_TYPE = 'text/event-stream';

/**
 * The server writes a heartbeat comment every 20 s; a client that has seen
 * nothing for this long treats the connection as dead and reconnects.
 */
export const REALTIME_SILENCE_TIMEOUT_MS = 45_000;

// ── SSE framing (WHATWG HTML § Server-sent events) ───────────────────────

/** Fields of one outgoing message. */
export interface SseFields {
  event?: string;
  data?: string;
  id?: string;
  /** Reconnection delay the client should use (ms). */
  retry?: number;
}

/** One dispatched message of a parsed stream. */
export interface SseMessage {
  /** The `event` field; `message` when the server sent none. */
  event: string;
  /** The `data` lines joined with `\n`. */
  data: string;
  /** The last `id` the stream set so far ('' when none). */
  lastEventId: string;
}

const LINE_BREAK = /\r\n|\r|\n/;

function singleLine(field: string, value: string): string {
  if (LINE_BREAK.test(value)) throw new Error(`SSE ${field} must be a single line`);
  return value;
}

/** Serialises one message; multi-line data becomes one `data:` line per line. */
export function formatSseMessage(fields: SseFields): string {
  let out = '';
  if (fields.retry !== undefined) out += `retry: ${Math.max(0, Math.floor(fields.retry))}\n`;
  if (fields.id !== undefined) out += `id: ${singleLine('id', fields.id)}\n`;
  if (fields.event !== undefined) out += `event: ${singleLine('event', fields.event)}\n`;
  if (fields.data !== undefined) {
    for (const line of fields.data.split(LINE_BREAK)) out += `data: ${line}\n`;
  }
  return `${out}\n`;
}

/** A comment (heartbeat): parsers ignore it, but it keeps proxies and the client's silence timer going. */
export function formatSseComment(text = ''): string {
  return `${text
    .split(LINE_BREAK)
    .map((line) => (line ? `: ${line}\n` : ':\n'))
    .join('')}\n`;
}

/** Wire form of a real-time event. */

export interface SseParserHandlers {
  onMessage(message: SseMessage): void;
  /** A comment line, without the leading `:` and one optional space. */
  onComment?(text: string): void;
  /** A valid `retry` field. */
  onRetry?(ms: number): void;
}

/**
 * Incremental parser for a decoded event stream: feed it text as it arrives
 * (`TextDecoder#decode(chunk, { stream: true })`). Handles CRLF/CR/LF line
 * ends (also when a CRLF is split between chunks), a leading BOM, comments,
 * fields without a value and multi-line data. Dependency-free, so the
 * clients can share it.
 */
export class SseParser {
  private buffer = '';
  private data = '';
  private eventType = '';
  private lastEventId = '';
  private started = false;
  /** The previous chunk ended with CR: a LF starting the next one belongs to it. */
  private pendingCR = false;

  constructor(private readonly handlers: SseParserHandlers) {}

  feed(chunk: string): void {
    if (chunk.length === 0) return;
    let text = chunk;
    if (!this.started) {
      this.started = true;
      if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
    }
    if (this.pendingCR) {
      this.pendingCR = false;
      if (text.startsWith('\n')) text = text.slice(1);
    }
    this.buffer += text;
    let start = 0;
    for (let index = 0; index < this.buffer.length; index += 1) {
      const code = this.buffer.charCodeAt(index);
      if (code !== 0x0a && code !== 0x0d) continue;
      const line = this.buffer.slice(start, index);
      if (code === 0x0d) {
        if (index + 1 < this.buffer.length) {
          if (this.buffer.charCodeAt(index + 1) === 0x0a) index += 1;
        } else {
          this.pendingCR = true;
        }
      }
      start = index + 1;
      this.processLine(line);
    }
    this.buffer = this.buffer.slice(start);
  }

  /** Forgets a partly received message (the stream ended or is being reconnected). */
  reset(): void {
    this.buffer = '';
    this.data = '';
    this.eventType = '';
    this.pendingCR = false;
    this.started = false;
  }

  private processLine(line: string): void {
    if (line === '') {
      this.dispatch();
      return;
    }
    if (line.startsWith(':')) {
      const comment = line.slice(1);
      this.handlers.onComment?.(comment.startsWith(' ') ? comment.slice(1) : comment);
      return;
    }
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    switch (field) {
      case 'event':
        this.eventType = value;
        break;
      case 'data':
        this.data += `${value}\n`;
        break;
      case 'id':
        if (!value.includes('\0')) this.lastEventId = value;
        break;
      case 'retry':
        if (/^\d+$/.test(value)) this.handlers.onRetry?.(Number(value));
        break;
      default:
        break;
    }
  }

  private dispatch(): void {
    if (this.data === '') {
      this.eventType = '';
      return;
    }
    const message: SseMessage = {
      event: this.eventType || 'message',
      data: this.data.endsWith('\n') ? this.data.slice(0, -1) : this.data,
      lastEventId: this.lastEventId,
    };
    this.data = '';
    this.eventType = '';
    this.handlers.onMessage(message);
  }
}

/**
 * A parsed message as a real-time event; null for event types this client
 * does not know (a newer server) and for malformed data.
 */
